import Multiselect from "multiselect-react-dropdown";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import "./EnterpriseStructureModal.scss";
import { Typeahead } from "react-bootstrap-typeahead";
import { withTranslation } from "react-i18next";
import { ReactMultiEmailInput } from "react-multi-email-input";
import "react-multi-email-input/dist/style.css";
import { toast } from "react-toastify";
import { BusinessUnitTypes, TaskStatus } from "../../../core/constants/common";
import CompanyDashboardService from "../../../core/service/CompanyDashboardService";
import CompanyService from "../../../core/service/CompanyService";
import UserService from "../../../core/service/UserService";
import UtilsService from "../../../core/service/UtilsService";
import Utils from "../../../core/utilities/Utils";
import ConfirmDialog from '../../../core/utilities/ConfirmPopup';
import useHasPermission from "../../../core/hooks/useHasPermission";
import { BsTrash } from "react-icons/bs";
import { MdAdd } from "react-icons/md";

const EnterpriseStructureModal = ({ t, id, showModal, parentId, modalData, countries, onSaveCallBack, onCloseCallback, parent }) => {	
	const [show, setShow] = useState(showModal ? true : false);	

	// To convert reviewers data to approvalSteps structure
	const convertReviewersToApprovalSteps = (reviewers) => {
		if (!reviewers || reviewers.length === 0) {
			return [{ id: 'step-1', level: 1, reviewers: [], emailNotification: false }];
		}
		
		return reviewers.sort((a, b) => a.level - b.level).reduce((steps, reviewer) => {
			const existingStep = steps.find(step => step.level === reviewer.level);
			if (existingStep) {
				existingStep.reviewers.push({
					id: reviewer.userId,
					name: reviewer.userName,
					login: reviewer.userLogin,
					email: reviewer.userEmail
				});
				// Preserve emailNotification if present at reviewer level (fallback to existing)
				if (typeof reviewer.emailNotification === 'boolean' && typeof existingStep.emailNotification !== 'boolean') {
					existingStep.emailNotification = reviewer.emailNotification;
				}
			} else {
				steps.push({
					id: `step-${reviewer.level}`,
					level: reviewer.level,
					emailNotification: typeof reviewer.emailNotification === 'boolean' ? reviewer.emailNotification : false,
					reviewers: [{
						id: reviewer.userId,
						name: reviewer.userName,
						login: reviewer.userLogin,
						email: reviewer.userEmail
					}]
				});
			}
			return steps;
		}, []);
	};

	const [formData, setFormData] = useState({

		name: modalData?.name ?? "",
		typeId: modalData?.unitType?.id ?? "",
		address: {
			stateId: modalData?.address?.state?.id ?? "",
			countryId: modalData?.address?.country?.id ?? "",
			building: modalData?.address?.building ?? "",
			street: modalData?.address?.street ?? "",
			zipCode: modalData?.address?.zipCode ?? "",
		},
		emails: modalData?.emails ?? [],
		reviewers: modalData?.reviewers ?? [],
		approvalSteps: convertReviewersToApprovalSteps(modalData?.reviewers),
		dataValidators: modalData?.dataValidators ?? [],
		dirtyFields: {},
		parentId: parentId ?? 0
	});
	const [statesList, setStatesList] = useState([]);
	const [validated, setValidated] = useState(false);
	const [openedFor, setOpenedFor] = useState(modalData?.openedFor ?? "NEW");
	const [deleteModal, setDeleteModal] = useState(false);
	const [displayData, setDisplayData] = useState(modalData);
	const [companyUsers, setCompanyUsers] = useState([]);
	const [dataValidators, setDataValidators] = useState([]);
	const [selectedValidators, setSelectedValidators] = useState([]);
	const { hasSettingsUpdatePermission } = useHasPermission();

	useEffect(() => {
		getEnterprise();
		getCompanyUsers();
		getDataValidators();
		if (openedFor !== "EDIT") return;

		getStatesForCountry(modalData?.address?.country?.id);
	}, [openedFor]);

	const getStatesForCountry = async (countryId) => {
		if (!countryId) return;

		UtilsService.getStateList(countryId)
			.then((response) => {
				setStatesList([...response.data]);
			})
			.catch((error) => { });
	};
	const getEnterprise = () => {
		if (!id) return;

		CompanyService.getEnterprise(id).then(res => {
			if (res.status !== 200) return;

			const data = res.data?.data;
			setFormData({
				name: data?.name ?? "",
				typeId: data?.unitType?.id ?? "",
				address: {
					stateId: data?.address?.state?.id ?? "",
					countryId: data?.address?.country?.id ?? "",
					building: data?.address?.building ?? "",
					street: data?.address?.street ?? "",
					zipCode: data?.address?.zipCode ?? "",
				},
				emails: data?.emails ?? [],
				reviewers: data?.reviewers ?? [],
				approvalSteps: convertReviewersToApprovalSteps(data?.reviewers),
				dataValidators: data?.dataValidators ?? [],
				parentId: parentId ?? 0
			});
			setDisplayData(data);
			// dataValidators is an array of user objects from backend
			const validatorUsers = data?.dataValidators ?? [];
			const validatorIds = validatorUsers.map(user => user.id);			
			setFormData(prev => ({
				...prev,
				dataValidators: validatorIds
			}));			
			// Set the selected validators for the dropdown
			setSelectedValidators(validatorUsers);			
			getStatesForCountry(data?.address?.country?.id);
		}).catch(err => { });
	};

	const handleClose = () => {
		setShow(false);
		onCloseCallback();
	};

	const handleSubmit = () => {
		const isRootBranch = !formData.parentId || formData.parentId === 0;
		if (!formData.name || !formData.typeId || (!isRootBranch && formData.emails.length === 0)) {
			setShow(true);
			setValidated(true);
			return;
		}		
		// Build reviewers array with proper level structure
		const reviewers = formData.approvalSteps
			.flatMap(step => step.reviewers.map(reviewer => ({
				userId: reviewer.id,
				level: step.level,
				emailNotification: !!step.emailNotification
			})));
		
		let payload = { 
			...formData,
			reviewers: reviewers
			// dataValidators is already updated in real-time in formData
		};		
		delete payload.approvalSteps;		
		if (id !== 0) {
			payload.id = id;
		}
		if (payload.parentId === 0) {
			payload.parentId = null;
		}
		const apiCall = id > 0
			? CompanyService.updateEnterprise(payload)
			: CompanyService.createEnterprise(payload);
		apiCall.then((response) => {
			if (response.status === 201 || response.status === 200) {
				if (response.data?.status === 'Failed') {
					toast.error(response.data?.message ?? 'Failed', {
						position: toast.POSITION.TOP_RIGHT,
					});
				} else {
					toast.success("Success", {
						position: toast.POSITION.TOP_RIGHT,
					});
				}
				setShow(false);
				onSaveCallBack();
			}
		}).catch((error) => {
		});
		setValidated(true);
	};

	const handleEditClick = () => {
		setOpenedFor("EDIT");
	};

	const onDeleteConfirm = () => {
		CompanyService.deleteEnterprise(id)
			.then((response) => {
				if (response.status !== 204) return;

				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				onSaveCallBack();
			})
			.catch((error) => { });
	};

	const onCountrySelect = (id) => {
		if (!id) return;

		setFormData({
			...formData,
			address: {
				...formData.address,
				countryId: id
			}
		});
		getStatesForCountry(id);
	};

	const renderViewOnlyData = () => {
		return (
			<div className="ViewEntity_card">
				<div className="ViewEntity_card_head">
					<Table size="sm" className="m-0">
						<tbody>
							<tr>
								<td>{t("Name.label")}</td>
								<td>:</td>
								<td><h6 className="fw-bold">{displayData?.name}</h6></td>
							</tr>
							<tr>
								<td>{t("Type.label")}</td>
								<td>:</td>
								<td><h6>{displayData?.unitType?.name}</h6></td>
							</tr>
							<tr>
								<td>{t("Code.label")}</td>
								<td>:</td>
								<td><h6>{displayData?.code} </h6></td>
							</tr>
							<tr>
								<td>{t("Address.label")}</td>
								<td>:</td>
								<td>
									<h6>
										{
											(displayData?.address?.building || displayData?.address?.street) &&
											`${displayData?.address?.building ?? ''} ${displayData?.address?.building ? ', ' : ''}${displayData?.address?.street ?? ','}`
										}
										{
											(displayData?.address?.city || displayData?.address?.state) &&
											`${displayData?.address?.city ?? ', '} ${displayData?.address?.city ? ', ' : ''}${displayData?.address?.state?.name ?? ''}`
										}
										{displayData?.address?.country && <span>, {displayData?.address?.country?.name}</span>}
										{displayData?.address?.zipCode && <span> - {displayData?.address?.zipCode}</span>}
									</h6>
								</td>
							</tr>
							{
								displayData?.emails &&
								<>
									<tr>
										<td>{t("Email.label")}</td>
										<td>:</td>
										<td>
											{
												displayData?.emails?.map(e => {
													return <h6>{e}</h6>;
												})
											}
										</td>
									</tr>
								</>
							}
							{
								displayData?.reviewers?.length > 0 &&
								<>
									{displayData?.reviewers
										.sort((a, b) => a.level - b.level)
										.reduce((steps, reviewer) => {
											const existingStep = steps.find(step => step.level === reviewer.level);
											if (existingStep) {
												existingStep.reviewers.push(reviewer);
												if (typeof reviewer.emailNotification === 'boolean' && typeof existingStep.emailNotification !== 'boolean') {
													existingStep.emailNotification = reviewer.emailNotification;
												}
											} else {
												steps.push({
													level: reviewer.level,
													emailNotification: typeof reviewer.emailNotification === 'boolean' ? reviewer.emailNotification : false,
													reviewers: [reviewer]
												});
											}
											return steps;
										}, [])
										.map((step) => (
											<tr key={step.level}>
												<td>{t("Step.label")} {step.level}</td>
												<td>:</td>
												<td>
													{step.reviewers.map((reviewer, index) => (
														<h6 key={index} className="mb-1">
															{reviewer.userName ?? ''}
															{index < step.reviewers.length - 1 && ', '}
														</h6>
													))}
													<div className="text-muted">
														{t("AllowEmailNotification.label")}: {step.emailNotification ? t("Yes.label") : t("No.label")}
													</div>
												</td>
											</tr>
										))
									}
								</>
							}
							{
								displayData?.dataValidators?.length > 0 &&
								<>
									<tr>
										<td>{t("DataValidators.label")}</td>
										<td>:</td>
										<td>
											{displayData?.dataValidators?.map((validatorId, index) => {
												// Find the user name from selectedValidators or show ID as fallback
												const user = selectedValidators.find(u => u.id === validatorId);
												return (
													<h6 key={index} className="mb-1">
														{user ? (user.name || user.userName) : `User ${validatorId}`}
														{index < displayData.dataValidators.length - 1 && ', '}
													</h6>
												);
											})}
										</td>
									</tr>
								</>
							}
						</tbody>
					</Table>
				</div>
			</div >
		);
	};

	const getCompanyUsers = () => {
		CompanyDashboardService.getUsers("")
			.then((response) => {
				setCompanyUsers([...response.data]);
			}).catch((error) => {
			});
	};

	const getDataValidators = () => {
		// Payload is an array of group names
		const payload = ["Update Data Management","Company Admin"];// You can customize these group names as needed
		
		UserService.getUsersByGroup(payload)
			.then((response) => {
				if (response.status === 200 && response.data) {
					setDataValidators([...response.data]);
				}
			}).catch((error) => {
				console.error('Error fetching data validators:', error);
			});
	};


	const addApprovalStep = () => {
		const newStep = {
			id: `step-${formData.approvalSteps.length + 1}`,
			level: formData.approvalSteps.length + 1,
			reviewers: [],
			emailNotification: false
		};
		setFormData({
			...formData,
			approvalSteps: [...formData.approvalSteps, newStep]
		});
	};

	const removeApprovalStep = (stepId) => {
		if (formData.approvalSteps.length <= 1) return; // Keep at least one step
		
		const updatedSteps = formData.approvalSteps
			.filter(step => step.id !== stepId)
			.map((step, index) => ({
				...step,
				id: `step-${index + 1}`,
				level: index + 1
			}));
		
		setFormData({
			...formData,
			approvalSteps: updatedSteps
		});
	};

	const updateApprovalStep = (stepId, selectedReviewers) => {
		
		const updatedSteps = formData.approvalSteps.map(step => 
			step.id === stepId ? { ...step, reviewers: selectedReviewers } : step
		);
		setFormData({
			...formData,
			approvalSteps: updatedSteps
		});
	};

	const renderApprovalSteps = () => {
		return (
			<div className="col-12">
				<label className="form-label mb-2">{t("DataApprovers.label")}</label>
				{formData.approvalSteps.map((step, index) => (
					<div key={step.id} className="enterprise-modal-approver-step">
						<div className="enterprise-modal-approver-level">
							<label className="form-label mb-0">
								{Utils.getOrdinalNumber(step.level)} {t("Level.label")}
							</label>
						</div>
						<div className="enterprise-modal-approver-fields">
							<Multiselect
								className="enterprise-modal-multiselect"
								options={companyUsers}
								selectedValues={step.reviewers}
								onSelect={(selectedList, selectedItem) => {
									const updatedReviewers = [...step.reviewers, selectedItem];
									updateApprovalStep(step.id, updatedReviewers);
								}}
								onRemove={(selectedList, removedItem) => {
									const updatedReviewers = step.reviewers.filter(reviewer => reviewer.id !== removedItem.id);
									updateApprovalStep(step.id, updatedReviewers);
								}}
								displayValue="name"
								placeholder={t("Select.label")}
								showCheckbox={true}
							/>
							<div className="form-check enterprise-modal-approver-notify">
								<input
									className="form-check-input"
									type="checkbox"
									id={`emailNotify-${step.id}`}
									checked={!!step.emailNotification}
									onChange={(e) => {
										const updatedSteps = formData.approvalSteps.map(s =>
											s.id === step.id ? { ...s, emailNotification: e.target.checked } : s
										);
										setFormData({
											...formData,
											approvalSteps: updatedSteps
										});
									}}
								/>
								<label className="form-check-label" htmlFor={`emailNotify-${step.id}`}>
									{t("AllowEmailNotification.label")}
								</label>
							</div>
						</div>
						<div className="enterprise-modal-approver-actions">
							{formData.approvalSteps.length > 1 && (
								<span
									className="cursor"
									role="button"
									tabIndex={0}
									onClick={() => removeApprovalStep(step.id)}
									onKeyDown={(e) => e.key === 'Enter' && removeApprovalStep(step.id)}
									title={t("RemoveStep.label")}
								>
									<BsTrash />
								</span>
							)}
							{index === formData.approvalSteps.length - 1 && formData.approvalSteps.length < 3 && (
								<span
									className="cursor"
									role="button"
									tabIndex={0}
									onClick={addApprovalStep}
									onKeyDown={(e) => e.key === 'Enter' && addApprovalStep()}
									title={t("AddStep.label")}
								>
									<MdAdd />
								</span>
							)}
						</div>
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			<Modal
				show={show}
				onHide={handleClose}
				size="lg"
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">
						{
							id > 0
								? openedFor === "VIEW" ? t("ViewEnterprise.label") : t("EditEnterprise.label")
								: t("CreateEnterprise.label")
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{
						openedFor === "VIEW"
							?
							<>{renderViewOnlyData()}</>
							:
							<Form noValidate validated={validated} className="enterprise-structure-modal">
								<div className="row g-3 m-0">
									<div className="col-md-6">
										<label className="form-label">{t("Name.label")} *</label>
										<input type="text"
											className="form-control"
											required
											maxLength={1000}
											onChange={(e) => setFormData({ ...formData, name: e.target.value })}
											value={formData.name} />
									</div>
									{parent && openedFor === "EDIT" ? (
										<div className="col-md-6">
											<label className="form-label">{t("PName.label")} *</label>
											<select
												className="form-select"
												onChange={(e) => setFormData({ ...formData, parentId: +e.target.value })}
												value={formData.parentId}
												required>
												<option value="" >{t("Select.label")}</option>
												{parent.map(b => (
													<option value={b.id} key={b.id}>{b.name}</option>
												))}
											</select>
										</div>
									) : (
										<div className="col-md-6">
											<label className="form-label">{t("Type.label")} *</label>
											<select
												className="form-select"
												onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
												value={formData.typeId}
												required>
												<option value="" disabled hidden>{t("Select.label")}</option>
												{
													BusinessUnitTypes.map(b => {
														return (
															<option value={b.id} key={b.id}>{b.name}</option>
														);
													})
												}
											</select>
										</div>
									)}
								</div>
								{parent && openedFor === "EDIT" && (
									<div className="row g-3 m-0">
										<div className="col-md-6">
											<label className="form-label">{t("Type.label")} *</label>
											<select
												className="form-select"
												onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
												value={formData.typeId}
												required>
												<option value="" disabled hidden>{t("Select.label")}</option>
												{
													BusinessUnitTypes.map(b => {
														return (
															<option value={b.id} key={b.id}>{b.name}</option>
														);
													})
												}
											</select>
										</div>
									</div>
								)}
								<div className="row g-3 m-0">
									<div className="col-12">
										<h6 className="enterprise-modal-section-title">{t("Address.label")}</h6>
									</div>
									<div className="col-md-4">
										<label className="form-label">{t("country.label")}</label>
										{countries?.length > 0 && (
											<Typeahead
												className={!formData.dirtyFields?.country || formData.address.countryId > 0 ? '' : 'invalid-typeahead'}
												id='countryfield'
												key='countryfield'
												clearButton
												labelKey={(options) => options.name}
												selected={countries?.filter((country) => country.id === formData.address.countryId) ?? []}
												onChange={(selected) => {
													if (selected[0]) {
														onCountrySelect(selected[0].id ?? 0);
													} else {
														setFormData({
															...formData,
															address: {
																...formData.address,
																countryId: "",
																stateId: ""
															}
														});
														setStatesList([]);
													}
												}}
												options={countries}
												placeholder={t("Select.label")}
												onInputChange={(val) =>
													setFormData({
														...formData,
														dirtyFields: {
															...formData.dirtyFields,
															country: true
														}
													})
												}
											/>
										)}
									</div>
									<div className="col-md-4">
										<label className="form-label">{t("state.label")}</label>
										{countries?.length > 0 && (
											<Typeahead
												className={!formData.dirtyFields?.state || formData.address.stateId > 0 ? '' : 'invalid-typeahead'}
												id='epStatefield'
												key='epStatefield'
												clearButton
												labelKey={(opt) => opt.name ?? ''}
												onChange={(selected) => {
													setFormData({
														...formData,
														address: {
															...formData.address,
															stateId: selected[0] ? selected[0].id : ""
														}
													});
												}}
												selected={statesList?.filter(s => s.id === formData.address?.stateId) ?? []}
												options={statesList}
												placeholder={t("Select.label")}
												onInputChange={(val) =>
													setFormData({
														...formData,
														dirtyFields: {
															...formData.dirtyFields,
															state: true
														}
													})
												}
											/>
										)}
									</div>
									<div className="col-md-4">
										<label className="form-label">{t("street.label")}</label>
										<input
											type="text"
											className="form-control"
											name="street"
											value={formData.address.street}
											onChange={(e) => setFormData({
												...formData,
												address: {
													...formData.address,
													street: e.target.value
												}
											})}
										/>
									</div>
									<div className="col-md-6">
										<label className="form-label">{t("building.label")}</label>
										<input
											type="text"
											className="form-control"
											name="building"
											value={formData.address.building}
											onChange={(e) => setFormData({
												...formData,
												address: {
													...formData.address,
													building: e.target.value
												}
											})}
										/>
									</div>
									<div className="col-md-6">
										<label className="form-label">{t("zipcode.label")}</label>
										<input
											type="text"
											className="form-control"
											name="zipCode"
											value={formData.address.zipCode}
											onChange={(e) => setFormData({
												...formData,
												address: {
													...formData.address,
													zipCode: e.target.value
												}
											})}
										/>
									</div>
								</div>
								<div className="row g-3 m-0">
									<div className="col-12">
										<label className="form-label">{t("DataOwners.label")} {formData.parentId > 0 && '*'}</label>
										<div className="enterprise-modal-data-owners-input">
											<ReactMultiEmailInput
												placeholder=""
												emails={formData.emails}
												setEmails={(emails) => setFormData({
													...formData,
													emails: emails
												})} />
										</div>
									</div>
									{renderApprovalSteps()}
									<div className="col-12">
										<label className="form-label">{t("DataValidators.label")}</label>
										<Multiselect
											className="enterprise-modal-multiselect"
											options={dataValidators}
											selectedValues={selectedValidators}
											onSelect={(selectedList, selectedItem) => {
												setSelectedValidators(selectedList);
												const validatorIds = selectedList.map(validator => validator.id);
												setFormData(prev => ({
													...prev,
													dataValidators: validatorIds
												}));
											}}
											onRemove={(selectedList, removedItem) => {
												setSelectedValidators(selectedList);
												const validatorIds = selectedList.map(validator => validator.id);
												setFormData(prev => ({
													...prev,
													dataValidators: validatorIds
												}));
											}}
											displayValue="name"
											placeholder={t("DataValidators.placeholder.label")}
											showCheckbox={true}
											emptyRecordMsg={t("DataValidators.noData.label")}
										/>
									</div>
								</div>
							</Form>
					}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleClose}>
						{t("Cancel.label")}
					</Button>
					{

						formData.status === TaskStatus.CLOSED
							? <></>
							: id > 0 && openedFor === "VIEW"
								?
								<>
									<Button variant="primary" hidden={!hasSettingsUpdatePermission()} onClick={() => handleEditClick()} className="px-3">
										{t("Edit.label")}
									</Button>
									<Button variant="primary" hidden={!hasSettingsUpdatePermission()} onClick={() => setDeleteModal(true)} className="px-3">
										{t("Delete.label")}
									</Button>
								</>

								:
								<Button variant="primary" onClick={() => handleSubmit()} >
									{id > 0 && openedFor === "EDIT" ? t("Update.label") : t("Create.label")}
								</Button>
					}
				</Modal.Footer>
			</Modal>
			{
				deleteModal &&
				<ConfirmDialog
					open={deleteModal}
					setOpen={(val) => setDeleteModal(val)}
					onConfirm={() => onDeleteConfirm()}                        >
					<div className='text-center'>{t("ConfirmDelete.label")}</div>
				</ConfirmDialog>
			}
		</>
	);
};

export default withTranslation()(EnterpriseStructureModal);
